import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  getAttachmentById,
  isTerminalAttachmentStatus,
  type Attachment,
} from '../api/attachment';
import {
  getParseRequestById,
  getParseResult,
  isTerminalParseStatus,
  type ParseRequest,
  type ParseRequestType,
} from '../api/parse-request';
import {
  PORTFOLIO_DETAIL_REFETCH_OPTIONS,
  PORTFOLIO_DETAIL_RETRY,
} from './use-portfolio-detail-queries';
import { deriveTimingFlags } from '../lib/poll-timing';
import type { ScopeContext } from '../../auth/model/types';

const SCAN_POLL_MS = 2_000;
const PARSE_POLL_MS = 3_000;

/**
 * Elapsed wall time since a poll started is the only signal a stalled pipeline has to
 * give. The backend's wire status collapses every internal processing stage into one
 * `processing` value, so there is no finer-grained progress to read from a single poll
 * response — only how long the same non-terminal answer has kept coming back. Two
 * minutes is short enough to be useful to a waiting user, long enough not to misread an
 * ordinarily slow parse as stuck, and well under `POLL_TIMEOUT_MS` below, which can
 * still revive a request this client has given up watching closely.
 *
 * `deriveTimingFlags` (`../lib/poll-timing.ts`) turns an elapsed reading and these two
 * constants into `stalled`/`timedOut`; `use-booking-status.ts`'s poll shares that same
 * function under its own, differently-derived thresholds — see the note there.
 */
const STALL_THRESHOLD_MS = 2 * 60_000;

/**
 * Unlike `STALL_THRESHOLD_MS`, which only changes the progress card's copy, crossing
 * `POLL_TIMEOUT_MS` stops the poll outright: `refetchInterval` returns `false`, the same
 * way it already does on an error or a terminal status. An unattended watcher otherwise
 * polls a pipeline that may never resolve on its own steam forever — roughly 30 req/min
 * on the scan poll, 20 req/min on the parse poll.
 *
 * This must stay GREATER than `STALL_THRESHOLD_MS` — both clocks share one anchor (see
 * `elapsedMs` in `useElapsedClock` below), and a ceiling shorter than the stall
 * threshold would time a poll out before the user ever sees the stalled hint. There is
 * no build-time check of that ordering (no precedent for one in this codebase), so read
 * both constants together before changing either; they are declared in that order,
 * smaller first, for the same reason.
 *
 * Ten minutes is not an arbitrary round number, and not a shorter number closer to
 * `STALL_THRESHOLD_MS`: it is when the backend first becomes *eligible* to recover the
 * request on its own — not "once its recovery cron has run", which is a later and less
 * certain event. The scan poll's ceiling matches the attachment cell's own retry window
 * (`SCAN_RETRY_WINDOW_MINUTES` in `b-cell-attachment-v1/handler/private/attachment/
 * scan_cron.py`, also ten minutes); the parse poll's matches the parser's dead-letter
 * window (`DEAD_LETTER_WINDOW_MINUTES` in `b-cell-parser-v1/util/constant.py`). Retry
 * covers the rest of the wait — backend recovery itself is expected to drop to seconds
 * under a separate change, so the client only needs to survive until the backend's own
 * cron has had one chance to run, not until recovery is likely to have finished by. Keep
 * the number at ten minutes rather than raising it.
 */
const POLL_TIMEOUT_MS = 10 * 60_000;

/**
 * One attempt per read, and no background refetching — the same policy the portfolio
 * detail queries state, imported rather than restated. A poll that fails must surface
 * its own retry rather than disappear into TanStack's backoff, because the user is
 * watching a pipeline and a silent stall is indistinguishable from slow work.
 */
const IMPORT_RETRY = PORTFOLIO_DETAIL_RETRY;
const IMPORT_REFETCH_OPTIONS = PORTFOLIO_DETAIL_REFETCH_OPTIONS;

/** One builder per query, so `useQuery` and any invalidation cannot drift apart. The
 *  parse keys carry the request type the id was read from: an id is unique across both
 *  request types, but the key names everything the fetch depends on, so a request
 *  polled under one type's path can never be served from a read made under the
 *  other's. */
export const parseImportQueryKeys = {
  attachment: (attachmentId: string) => ['portfolio-import-attachment', attachmentId] as const,
  parseRequest: (requestType: ParseRequestType | null, requestId: string) =>
    ['portfolio-import-parse-request', requestType, requestId] as const,
  parseResult: (requestType: ParseRequestType | null, requestId: string) =>
    ['portfolio-import-parse-result', requestType, requestId] as const,
};

/**
 * Owns the elapsed-time anchor's refs for one poll and exposes them as plain functions —
 * `elapsedMs`, `resetElapsed`, `markAdopted` — rather than a ready-computed `stalled` /
 * `timedOut` pair. Called BEFORE `useQuery` in each of the two exported hooks below, so
 * `refetchInterval` — built as part of that very next `useQuery` call — can call
 * `elapsedMs(query.state.data)` directly at decision time, closing over the same refs
 * this hook owns.
 *
 * (An earlier shape computed `stalled`/`timedOut` in a hook called AFTER `useQuery` — it
 * needs `query.isError` and `query.data`, which only exist once `useQuery` has run — and
 * bridged the resulting ordering problem with a `pastCeilingRef` written mid-render and
 * read back by `refetchInterval` on a later evaluation. Calling this hook first removes
 * the ordering problem, and the ref workaround, entirely: `refetchInterval`'s closure is
 * still fixed at the `useQuery` call — TanStack never re-creates it — but the closure
 * already has everything it needs the moment it is built, so nothing has to be written
 * back into it later. `refetchInterval` itself is not re-invoked on a timer tick; the
 * option is re-evaluated by TanStack's `setOptions` (its own post-render effect) and by
 * `onQueryUpdate` (fired whenever the query's cached state changes, including the moment
 * a fetch settles) — the interval timer only triggers the next fetch, it never reads
 * this option itself.)
 *
 * The clock anchors to whichever is LATER: the resource's own `created` timestamp, or the
 * moment of the last manual Retry. Once the first read has landed, `created` — never
 * local watch time — is the base anchor, because `id` does not only become non-null on a
 * fresh dispatch: the `already-exists` recovery path in `use-csv-import.ts` can set it to
 * an **adopted, already-old** resource (a retried timeout, a re-upload after closing the
 * tab), and that resource must not read as freshly started just because this browser only
 * just discovered it. But `created` is immutable and never advances on its own, so
 * anchoring to it alone would let one old timestamp outvote every future retry: a poll
 * that errors well past the threshold and is retried, then succeeds with a still-non-
 * terminal answer, would jump straight back to the stalled copy the abandoned attempt had
 * already earned, instead of showing the plain "checking" state Retry is supposed to
 * produce. `resetElapsed` (called on retry) records that moment in `rearmedAtRef`, and the
 * effective anchor is the later of the two — so a genuinely stalled, never-retried import
 * still reads as old (this is what keeps an adopted resource correctly stalled), while a
 * fresh Retry always rearms the clock regardless of how old `created` is. While `data` is
 * still `undefined` — the id is set but nothing has come back yet — there is no backend
 * timestamp to read, so local watch time (`startedAtRef`, seeded on `id` change) stands in
 * for `created` in the same later-of comparison, and `resetElapsed` re-seeds it too.
 *
 * Adoption is the same kind of rearm as a manual Retry — reconnecting to a recorded
 * resource after "Start over" and the same file is the user's own deliberate re-upload,
 * not the discovery of some stale background resource — but it cannot reuse
 * `resetElapsed` directly. Adoption calls the id-setter (`setAttachmentId` /
 * `setRequestId` in `use-csv-import.ts`) with an id that is the ALREADY-OLD adopted
 * resource's own id, and the `[id]` effect below runs on every id change and would seed
 * `rearmedAtRef` from scratch for it — a plain `resetElapsed()` call made just before the
 * id-setter would be overwritten the moment that effect runs. `markAdopted` records the
 * rearm in a third ref (`adoptedAtRef`) instead, set before the id-setter is called; the
 * `[id]` effect then seeds `rearmedAtRef` FROM `adoptedAtRef` (rather than always nulling
 * it) and clears `adoptedAtRef` again, so the rearm survives the very reset that would
 * otherwise erase it. A normal fresh-upload id change never calls `markAdopted`, so
 * `adoptedAtRef` stays `null` and the effect's behavior on that path is unchanged: no
 * rearm, `created` (once it arrives) anchors the clock on its own.
 *
 * `requireObject` (see `shared/lib/response-shape.ts`) only proves the top-level key is an
 * object — nothing validates `created` field by field — so a `created` that is missing,
 * `null`, or not a parseable instant falls back to local watch time exactly like `data`
 * itself being undefined, instead of silently anchoring the clock to the Unix epoch (from
 * `null`) or leaking a `NaN` into the stall comparison (from a garbled string).
 *
 * This whole comparison depends on the server's `created` and the browser's `Date.now()`
 * agreeing to within the threshold's own margin — a client clock skewed further than that
 * can misjudge a normal poll as stalled, or the reverse.
 */
function useElapsedClock(id: string | null): {
  elapsedMs: (data?: { created?: string | null }) => number | null;
  resetElapsed: () => void;
  markAdopted: () => void;
} {
  const startedAtRef = useRef<number | null>(null);
  const rearmedAtRef = useRef<number | null>(null);
  // Set by `markAdopted`, consumed and cleared by the `[id]` effect below — see the
  // "Adoption" paragraph above for why this needs a third ref rather than reusing
  // `resetElapsed`.
  const adoptedAtRef = useRef<number | null>(null);
  useEffect(() => {
    startedAtRef.current = id ? Date.now() : null;
    rearmedAtRef.current = adoptedAtRef.current;
    adoptedAtRef.current = null;
  }, [id]);

  const resetElapsed = () => {
    startedAtRef.current = Date.now();
    rearmedAtRef.current = Date.now();
  };

  const markAdopted = () => {
    adoptedAtRef.current = Date.now();
  };

  const elapsedMs = (data?: { created?: string | null }): number | null => {
    // `?? NaN` keeps a missing or `null` `created` out of `Date`'s own null-coercion —
    // `new Date(null)` resolves to the Unix epoch, not an invalid date — so every
    // unreadable shape (absent, `null`, or a string `Date` cannot parse) lands on the
    // same `NaN`, and `Number.isFinite` below is what actually decides real-timestamp
    // vs. fall back.
    const createdAt: number | null = data ? new Date(data.created ?? NaN).getTime() : null;
    const anchoredAt: number | null = createdAt != null && Number.isFinite(createdAt)
      ? createdAt
      : startedAtRef.current;
    const effectiveStartedAt = anchoredAt == null
      ? rearmedAtRef.current
      : rearmedAtRef.current == null
        ? anchoredAt
        : Math.max(anchoredAt, rearmedAtRef.current);
    return effectiveStartedAt == null ? null : Date.now() - effectiveStartedAt;
  };

  return { elapsedMs, resetElapsed, markAdopted };
}

/**
 * Polls the uploaded file until the malware scan reaches a verdict.
 *
 * Polling stops on any terminal status *and* on an error. Stopping on error is
 * deliberate: with `retry: false` a failed read leaves `data` undefined, so a
 * status-only stop condition would poll a broken endpoint forever while the screen
 * showed "checking" — a stall dressed up as progress. The caller reports the failure
 * and offers `refetch` instead.
 */
export function useAttachmentScanQuery(
  sessionId: string | null,
  accountScope: ScopeContext | null,
  attachmentId: string | null,
) {
  const clock = useElapsedClock(attachmentId);

  const query = useQuery({
    queryKey: parseImportQueryKeys.attachment(attachmentId ?? ''),
    queryFn: () => getAttachmentById(sessionId!, accountScope!, attachmentId!),
    enabled: !!sessionId && !!accountScope && !!attachmentId,
    retry: IMPORT_RETRY,
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      const attachment = query.state.data as Attachment | undefined;
      if (isTerminalAttachmentStatus(attachment?.status)) return false;
      // The ceiling only applies to a SETTLED read — see `deriveTimingFlags` above for
      // why a still-fetching read must not stop the poll on an elapsed reading of the
      // answer it is about to replace. This callback is re-evaluated once that fetch
      // settles (`onQueryUpdate`), so skipping here costs at most one extra tick.
      if (query.state.fetchStatus === 'fetching') return SCAN_POLL_MS;
      const elapsed = clock.elapsedMs(attachment);
      if (elapsed != null && elapsed > POLL_TIMEOUT_MS) return false;
      return SCAN_POLL_MS;
    },
    ...IMPORT_REFETCH_OPTIONS,
  });

  const isTerminal = isTerminalAttachmentStatus(query.data?.status);
  const isActive = !!attachmentId && !query.isError && !isTerminal;
  const { stalled, timedOut } = deriveTimingFlags(
    clock.elapsedMs(query.data), isActive, query.isFetching, STALL_THRESHOLD_MS, POLL_TIMEOUT_MS,
  );

  return { ...query, stalled, timedOut, resetElapsed: clock.resetElapsed, markAdopted: clock.markAdopted };
}

/**
 * Polls the parse request until it settles.
 *
 * Asks for `include_fields=results` on every read so the row counts are available the
 * instant the request turns terminal, rather than needing a second round trip before
 * the outcome can be described. `requestType` selects the path the id is polled
 * under — each request type has its own `/{id}` route — and is the panel's
 * `activeImport`, which is always set while a `requestId` is.
 */
export function useParseRequestQuery(
  sessionId: string | null,
  accountScope: ScopeContext | null,
  requestType: ParseRequestType | null,
  requestId: string | null,
) {
  const clock = useElapsedClock(requestId);

  const query = useQuery({
    queryKey: parseImportQueryKeys.parseRequest(requestType, requestId ?? ''),
    queryFn: () => getParseRequestById(sessionId!, accountScope!, requestType!, requestId!),
    enabled: !!sessionId && !!accountScope && !!requestType && !!requestId,
    retry: IMPORT_RETRY,
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      const request = query.state.data as ParseRequest | undefined;
      if (isTerminalParseStatus(request?.status)) return false;
      // The ceiling only applies to a SETTLED read — see `deriveTimingFlags` above for
      // why a still-fetching read must not stop the poll on an elapsed reading of the
      // answer it is about to replace. This callback is re-evaluated once that fetch
      // settles (`onQueryUpdate`), so skipping here costs at most one extra tick.
      if (query.state.fetchStatus === 'fetching') return PARSE_POLL_MS;
      const elapsed = clock.elapsedMs(request);
      if (elapsed != null && elapsed > POLL_TIMEOUT_MS) return false;
      return PARSE_POLL_MS;
    },
    ...IMPORT_REFETCH_OPTIONS,
  });

  const isTerminal = isTerminalParseStatus(query.data?.status);
  const isActive = !!requestId && !query.isError && !isTerminal;
  const { stalled, timedOut } = deriveTimingFlags(
    clock.elapsedMs(query.data), isActive, query.isFetching, STALL_THRESHOLD_MS, POLL_TIMEOUT_MS,
  );

  return { ...query, stalled, timedOut, resetElapsed: clock.resetElapsed, markAdopted: clock.markAdopted };
}

/**
 * The parsed rows — fetched only when the caller will actually render row content.
 *
 * Two gates compose into `enabled`. Terminality: `GET /{id}/result` answers a 400 for
 * a request still at `created` or `processing`, so it is a precondition of the call
 * rather than a display detail. Need: the response carries every raw row, on the order
 * of the uploaded file itself, while the headline counts are already delivered free by
 * the poll's `include_fields=results` summaries — so the caller passes `isNeeded` only
 * when there are rejected rows to list (or when no summaries arrived and the counts
 * must be derived from the rows themselves).
 */
export function useParseResultQuery(
  sessionId: string | null,
  accountScope: ScopeContext | null,
  requestType: ParseRequestType | null,
  requestId: string | null,
  isNeeded: boolean,
) {
  return useQuery({
    queryKey: parseImportQueryKeys.parseResult(requestType, requestId ?? ''),
    queryFn: () => getParseResult(sessionId!, accountScope!, requestType!, requestId!),
    enabled: !!sessionId && !!accountScope && !!requestType && !!requestId && isNeeded,
    retry: IMPORT_RETRY,
    ...IMPORT_REFETCH_OPTIONS,
  });
}
